/*
 * `Intl`, and nothing else — S4.6.
 *
 * No date library. The two things this page formats are an instant and an age,
 * and the platform has both: `Intl.DateTimeFormat` and `Intl.RelativeTimeFormat`
 * are built in, weigh nothing, and are what `DESIGN_SYSTEM.md` §7.17 already
 * specifies for every other surface. Adding 12 kB of date library to a 75 kB
 * budget to reformat one timestamp would be the wrong trade twice over.
 *
 * English only (D6), so the locale is pinned to `en-GB` rather than read from
 * the browser: a page that renders half its dates in the reader's locale and all
 * its prose in English is not localized, it is inconsistent. `en-GB` because it
 * gives 24-hour time, which is what a UTC instant beside a server log should be.
 */

const UTC_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/*
 * The full instant, to the second, for a timeline entry.
 *
 * Time-only was the first attempt and it is wrong here: an incident that opens
 * at 22:40 and updates at 00:01 renders two stamps whose order is not obvious
 * and whose gap could be eighty minutes or twenty-four hours. A timeline is
 * exactly the place §7.17's "the absolute instant is always present" was written
 * for — it is read beside server logs by people comparing two clocks — so the
 * date stays on every row rather than being inferred from a heading that does
 * not exist.
 *
 * Seconds, because an update timeline is the one surface where the order of two
 * entries a minute apart is the information.
 */
const UTC_TIMELINE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

/** `null` rather than a thrown error or "Invalid Date" on screen. */
function parse(isoInstant: string): Date | null {
  const value = Date.parse(isoInstant)
  return Number.isNaN(value) ? null : new Date(value)
}

/** `28 Aug 2026, 14:23 UTC`. The instant, always present (§7.17). */
export function absoluteUtc(isoInstant: string): string {
  const date = parse(isoInstant)
  return date === null ? isoInstant : `${UTC_FORMAT.format(date)} UTC`
}

/** `28 Aug 2026, 00:01:52 UTC` — a timeline entry. See the formatter above. */
export function timelineUtc(isoInstant: string): string {
  const date = parse(isoInstant)
  return date === null ? isoInstant : `${UTC_TIMELINE.format(date)} UTC`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * `2 minutes ago`, or `null` when the instant will not parse.
 *
 * `null` and not a fallback string: this is the supplementary half of §7.17's
 * pair, and omitting it leaves the absolute instant standing alone, which is
 * still complete. Inventing "some time ago" would not be.
 */
export function relative(isoInstant: string, now: number = Date.now()): string | null {
  const date = parse(isoInstant)
  if (date === null) return null
  const elapsed = date.getTime() - now

  const magnitude = Math.abs(elapsed)
  if (magnitude < MINUTE) return RELATIVE.format(Math.round(elapsed / 1_000), 'second')
  if (magnitude < HOUR) return RELATIVE.format(Math.round(elapsed / MINUTE), 'minute')
  if (magnitude < DAY) return RELATIVE.format(Math.round(elapsed / HOUR), 'hour')
  return RELATIVE.format(Math.round(elapsed / DAY), 'day')
}

/**
 * How long an incident lasted, or how long it has been running.
 *
 * Rendered in whole minutes below an hour and whole hours above it: an incident
 * duration to the second is precision nobody acts on, and it invites a reader to
 * compare two numbers that were rounded differently.
 */
export function duration(startIso: string, endIso: string | null, now: number = Date.now()): string | null {
  const start = parse(startIso)
  if (start === null) return null
  const end = endIso === null ? now : parse(endIso)?.getTime()
  if (end === undefined) return null

  const elapsed = end - start.getTime()
  if (elapsed < 0) return null
  if (elapsed < HOUR) {
    const minutes = Math.max(1, Math.round(elapsed / MINUTE))
    return `${minutes} min`
  }
  const hours = Math.floor(elapsed / HOUR)
  const minutes = Math.round((elapsed % HOUR) / MINUTE)
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`
}
